import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { NAV } from "@/config/modules";
import { canAccess } from "@/lib/rbac";
import { Button } from "@/components/ui/button";
import { LoadingState } from "@/components/common/States";
import { fmtNumber } from "@/lib/format";
import {
  ClipboardCheck, CalendarDays, ChevronRight, FileBarChart, ClipboardList,
  TrendingUp, AlertTriangle, ListTodo,
} from "lucide-react";

const asArray = (v) => (Array.isArray(v) ? v : Array.isArray(v?.items) ? v.items : []);
const isToday = (dt) => {
  if (!dt) return false;
  const d = new Date(dt);
  return !isNaN(d) && d.toDateString() === new Date().toDateString();
};
const isOpen = (s) => !["closed", "done", "completed", "resolved", "verified"].includes(String(s || "").toLowerCase());

function KpiTile({ icon: Icon, label, value, accent, testId }) {
  const bar = {
    amber: "from-amber-600 to-amber-400", emerald: "from-emerald-600 to-emerald-400",
    rose: "from-rose-600 to-rose-400", sky: "from-sky-600 to-sky-400",
  }[accent];
  return (
    <div data-testid={testId} className="relative overflow-hidden rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${bar}`} />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">{label}</div>
          <div className="mt-2 font-display text-3xl font-bold text-stone-900">{value}</div>
        </div>
        <Icon className="h-5 w-5 text-stone-400" />
      </div>
    </div>
  );
}

function ModuleCard({ item, onClick }) {
  const Icon = item.icon;
  return (
    <button
      data-testid={`dash-card-${item.testId}`}
      onClick={onClick}
      className="group flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-4 text-left shadow-sm transition-all hover:border-amber-300 hover:shadow-md"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-700">
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1 truncate font-medium text-stone-800">{item.label}</span>
      <ChevronRight className="h-4 w-4 shrink-0 text-stone-300 transition-colors group-hover:text-amber-500" />
    </button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const { data: auditsRaw, isLoading: la } = useResource("/audits");
  const { data: capaRaw, isLoading: lc } = useResource("/capa");

  const audits = useMemo(() => asArray(auditsRaw), [auditsRaw]);
  const capa = useMemo(() => asArray(capaRaw), [capaRaw]);

  const todayAudits = audits.filter((a) => isToday(a.dateTime || a.createdAt));
  const scored = todayAudits.filter((a) => typeof a.overallScore === "number");
  const avgScore = scored.length
    ? Math.round(scored.reduce((s, a) => s + (a.overallScore || 0), 0) / scored.length)
    : null;
  const criticalIssues = todayAudits.reduce((s, a) => s + (a.criticalCount || 0), 0);
  const openActions = capa.filter((c) => isOpen(c.status)).length;

  const weeklyAudits = audits
    .filter((a) => /week/i.test(String(a.auditType || a.frequency || "")))
    .sort((x, y) => new Date(y.dateTime || y.createdAt || 0) - new Date(x.dateTime || x.createdAt || 0));
  const lastWeekly = weeklyAudits[0];

  const today = new Date().toDateString();

  const cardGroups = NAV.map((g) => ({
    group: g.group,
    items: g.items.filter(
      (it) => canAccess(role, it.roles) && !["/", "/daily-audit", "/weekly-audit"].includes(it.path)
    ),
  })).filter((g) => g.items.length > 0);

  if (la || lc) {
    return (
      <div className="space-y-6">
        <div>
          <h1 data-testid="page-title" className="font-display text-2xl font-bold tracking-tight text-stone-900">Bakery Operations Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">Freshezy Foodtech · {today}</p>
        </div>
        <LoadingState label="Loading dashboard…" rows={4} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 data-testid="page-title" className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-stone-900">
          Bakery Operations Dashboard
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Freshezy Foodtech · {today}</p>
      </div>

      {/* Audit CTAs */}
      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Button
          data-testid="start-daily-audit"
          onClick={() => navigate("/daily-audit")}
          className="h-14 gap-2 bg-emerald-700 text-base font-semibold hover:bg-emerald-800"
        >
          <ClipboardCheck className="h-5 w-5" /> START DAILY AUDIT
        </Button>
        <Button
          data-testid="start-weekly-audit"
          variant="outline"
          onClick={() => navigate("/weekly-audit")}
          className="h-14 gap-2 border-stone-300 text-base font-semibold"
        >
          <CalendarDays className="h-5 w-5" /> START WEEKLY AUDIT
        </Button>
      </section>

      {/* Today KPIs */}
      <section>
        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-stone-400">Today · Daily</div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KpiTile testId="kpi-daily-audits" icon={ClipboardList} accent="emerald" label="Daily Audits" value={fmtNumber(todayAudits.length, 0)} />
          <KpiTile testId="kpi-avg-score" icon={TrendingUp} accent="sky" label="Average Score" value={avgScore == null ? "—" : `${avgScore}%`} />
          <KpiTile testId="kpi-critical-issues" icon={AlertTriangle} accent="rose" label="Critical Issues" value={fmtNumber(criticalIssues, 0)} />
          <KpiTile testId="kpi-open-actions" icon={ListTodo} accent="amber" label="Open Actions" value={fmtNumber(openActions, 0)} />
        </div>
      </section>

      {/* Last weekly + Reports */}
      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm lg:col-span-2">
          <div className="mb-3 flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-amber-600" />
            <h3 className="font-display text-base font-semibold text-stone-900">Last Weekly Audit</h3>
          </div>
          {lastWeekly ? (
            <button
              data-testid="last-weekly-audit"
              onClick={() => navigate("/weekly-audit")}
              className="flex w-full items-center justify-between rounded-lg border border-stone-100 bg-stone-50/60 px-4 py-3 text-left hover:border-amber-200"
            >
              <div>
                <div className="text-sm font-medium text-stone-800">{lastWeekly.bakeryName || lastWeekly.auditType || "Weekly Audit"}</div>
                <div className="text-[11px] text-muted-foreground">
                  {lastWeekly.auditorName || "—"} · {new Date(lastWeekly.dateTime || lastWeekly.createdAt).toLocaleDateString()}
                </div>
              </div>
              <div className="font-display text-lg font-bold text-stone-900">
                {typeof lastWeekly.overallScore === "number" ? `${Math.round(lastWeekly.overallScore)}%` : "—"}
              </div>
            </button>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">No weekly audit yet. Tap START WEEKLY AUDIT.</p>
          )}
        </div>

        <button
          data-testid="dash-reports"
          onClick={() => navigate("/reports")}
          className="group flex flex-col justify-between rounded-xl border border-stone-200 bg-white p-5 text-left shadow-sm transition-all hover:border-amber-300 hover:shadow-md"
        >
          <div className="flex items-center gap-2">
            <FileBarChart className="h-5 w-5 text-amber-700" />
            <h3 className="font-display text-base font-semibold text-stone-900">Reports</h3>
          </div>
          <div className="mt-2">
            <div className="text-sm font-medium text-stone-800">View all audit reports</div>
            <div className="text-[11px] text-muted-foreground">Export, filter and email past audits</div>
          </div>
          <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-amber-700">
            Open <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        </button>
      </section>

      {/* Module launcher grid */}
      {cardGroups.map((g) => (
        <section key={g.group}>
          <div className="mb-3 text-[11px] font-bold uppercase tracking-wider text-stone-400">{g.group}</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {g.items.map((it) => (
              <ModuleCard key={it.path} item={it} onClick={() => navigate(it.path)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
